import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { ApiResponse, Category } from '../../types';
import { API_CONFIG } from '../../config';
import { readApiResponse } from '../../lib/utils/apiResponse';

const API_URL = API_CONFIG.API_URL;

interface CategoriesState {
  items: Category[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  filterItems: Category[];
  filterLoading: boolean;
  filterPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const initialState: CategoriesState = {
  items: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 1,
  },
  filterItems: [],
  filterLoading: false,
  filterPagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 1,
  },
};

export const fetchCategories = createAsyncThunk(
  'categories/fetchAll',
  async (params?: { pageNumber?: number; pageSize?: number; search?: string; sortBy?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    
    const url = `${API_URL}/categories${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    const data: ApiResponse<any> = await readApiResponse(response);
    return data.result || { data: [], pagination: {} };
  }
);

export const fetchCategoriesForFilter = createAsyncThunk(
  'categories/fetchForFilter',
  async (params?: { pageNumber?: number; pageSize?: number; append?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    
    const url = `${API_URL}/categories${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    const data: ApiResponse<any> = await readApiResponse(response);
    return { 
      result: data.result || { data: [], pagination: {} },
      append: params?.append || false
    };
  }
);

export const fetchTrashedCategories = createAsyncThunk(
  'categories/fetchTrashed',
  async (params?: { pageNumber?: number; pageSize?: number; search?: string; sortBy?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);

    const token = localStorage.getItem('accessToken');
    const url = `${API_URL}/categories/trashed${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    const data: ApiResponse<any> = await readApiResponse(response);
    return data.result || { data: [], pagination: {} };
  }
);

export const createCategory = createAsyncThunk('categories/create', async (name: string) => {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_URL}/categories`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify({ name }),
  });
  const data: ApiResponse<Category> = await readApiResponse(response);
  return data.result;
});

export const deleteCategory = createAsyncThunk('categories/delete', async (id: string) => {
  const token = localStorage.getItem('accessToken');
  await fetch(`${API_URL}/categories/${id}`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return id;
});

export const restoreCategory = createAsyncThunk('categories/restore', async (id: string) => {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_URL}/categories/${id}/restore`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  const data: ApiResponse<Category> = await readApiResponse(response);
  return data.result;
});

export const forceDeleteCategory = createAsyncThunk('categories/forceDelete', async (id: string) => {
  const token = localStorage.getItem('accessToken');
  await fetch(`${API_URL}/categories/${id}/force`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return id;
});

export const updateCategory = createAsyncThunk(
  'categories/update',
  async ({ id, name, order }: { id: string; name: string; order?: number }) => {
    const body: { name: string; order?: number } = { name };
    if (order !== undefined) {
      body.order = order;
    }

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_URL}/categories/${id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
    });
    const data: ApiResponse<Category> = await readApiResponse(response);
    return data.result;
  }
);

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    resetFilterCategories: (state) => {
      state.filterItems = [];
      state.filterPagination = initialState.filterPagination;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch';
      })
      .addCase(fetchCategoriesForFilter.pending, (state) => {
        state.filterLoading = true;
      })
      .addCase(fetchCategoriesForFilter.fulfilled, (state, action) => {
        state.filterLoading = false;
        const data = action.payload.result.data || action.payload.result;
        if (action.payload.append) {
          state.filterItems = [...state.filterItems, ...data];
        } else {
          state.filterItems = data;
        }
        state.filterPagination = action.payload.result.pagination || state.filterPagination;
      })
      .addCase(fetchCategoriesForFilter.rejected, (state) => {
        state.filterLoading = false;
      })
      .addCase(fetchTrashedCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTrashedCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchTrashedCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch trashed';
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        if (action.payload) {
          state.items.push(action.payload);
        }
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(restoreCategory.fulfilled, (state, action) => {
        if (action.payload) {
          state.items = state.items.filter(item => item.id !== action.payload!.id);
        }
      })
      .addCase(forceDeleteCategory.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.items.findIndex(item => item.id === action.payload!.id);
          if (index !== -1) {
            state.items[index] = action.payload;
          }
        }
      });
  },
});

export const { resetFilterCategories } = categoriesSlice.actions;
export default categoriesSlice.reducer;
